<script>
import ModalSimulatorResultAnswersComponent from '../../features/modals/ModalSimulatorResultAnswersComponent';

export default {
  name: 'ModalSimulatorResultAnswersView',
  extends: ModalSimulatorResultAnswersComponent,
  computed: {
    questions() {
      return this.formData?.simulatorQuestions || [];
    },
    totalQuestions() {
      return this.questions.length;
    },
    totalCorrect() {
      return this.questions.filter((q) =>
        this.isAnswerSuccess(q.selectedAnswers, q.successAnswers)
      ).length;
    },
    totalIncorrect() {
      return Math.max(0, this.totalQuestions - this.totalCorrect);
    },
    accuracyPercentage() {
      if (!this.totalQuestions) return 0;
      return Math.round((this.totalCorrect / this.totalQuestions) * 100);
    },
  },
  methods: {
    getOptionLetter(index) {
      return String.fromCharCode(65 + index);
    },
    isOptionTargetCorrect(option, successAnswers) {
      if (option && option.success) return true;
      if (Array.isArray(successAnswers) && option) {
        return successAnswers.some(
          (sa) => sa.answer === option.answer && (sa.success === true || sa.success === undefined)
        );
      }
      return false;
    },
    getOptionClass(option, successAnswers) {
      const isCorrect = this.isOptionTargetCorrect(option, successAnswers);
      if (option && option.isSelected && isCorrect) return 'is_selected_correct';
      if (option && option.isSelected && !isCorrect) return 'is_selected_wrong';
      if (option && !option.isSelected && isCorrect) return 'is_target_correct';
      return 'is_neutral';
    },
  },
};
</script>

<template>
  <div>
    <el-dialog
      v-model="conf.isVisible"
      class="subscription-account-modal section-results-modal"
      width="880px"
      :before-close="onCancel"
      :close-on-click-modal="false"
      append-to-body
    >
      <template #header>
        <div class="student_modal_header">
          <div class="student_modal_header_icon">
            <i class="fas fa-clipboard-check" />
          </div>
          <div class="student_modal_header_text">
            <h3 class="student_modal_title">
              Resultados Detallados de la Sección
            </h3>
            <p class="student_modal_subtitle">
              Revisa el desglose de preguntas, tus selecciones y las respuestas correctas
            </p>
          </div>
        </div>
      </template>

      <div v-loading="conf.isLoading" class="section_results_body">
        <!-- Resumen de desempeño y leyenda -->
        <div class="section_results_summary_card">
          <div class="section_results_stats_row">
            <div class="section_results_stat_item">
              <span class="stat_label">Total Preguntas</span>
              <span class="stat_value">{{ totalQuestions }}</span>
            </div>
            <div class="section_results_stat_item">
              <span class="stat_label">Aciertos</span>
              <span class="stat_value is_success">{{ totalCorrect }}</span>
            </div>
            <div class="section_results_stat_item">
              <span class="stat_label">Incorrectas</span>
              <span class="stat_value is_danger">{{ totalIncorrect }}</span>
            </div>
            <div class="section_results_stat_item">
              <span class="stat_label">Efectividad</span>
              <span class="stat_value is_primary">{{ accuracyPercentage }}%</span>
            </div>
          </div>

          <div class="section_results_legend">
            <span class="legend_title">Guía de revisión:</span>
            <span class="legend_pill is_correct">
              <i class="fas fa-check-circle" /> Tu respuesta correcta
            </span>
            <span class="legend_pill is_incorrect">
              <i class="fas fa-times-circle" /> Tu respuesta incorrecta
            </span>
            <span class="legend_pill is_selected">
              <i class="fas fa-check" /> Opción correcta
            </span>
          </div>
        </div>

        <!-- Tabla de preguntas -->
        <div class="section_results_table_card">
          <el-table
            :data="questions"
            style="width: 100%"
            max-height="420"
            class="section_results_table"
          >
            <!-- Detalle expandible -->
            <el-table-column type="expand" width="48">
              <template #default="props">
                <div class="question_detail_card">
                  <div class="question_detail_header">
                    <span class="detail_title">
                      <i class="fas fa-list-ol" /> Desglose de opciones de la pregunta
                    </span>
                    <span
                      v-if="isAnswerSuccess(props.row.selectedAnswers, props.row.successAnswers)"
                      class="question_status_badge is_success"
                    >
                      <i class="fas fa-check-circle" /> Respuesta Acertada
                    </span>
                    <span
                      v-else
                      class="question_status_badge is_danger"
                    >
                      <i class="fas fa-times-circle" /> Respuesta No Acertada
                    </span>
                  </div>

                  <div class="question_statement_box" v-html="props.row.question" />

                  <div class="question_options_list">
                    <div
                      v-for="(item, index) in props.row.selectedAnswers"
                      :key="index"
                      class="question_option_item"
                      :class="getOptionClass(item, props.row.successAnswers)"
                    >
                      <div class="option_left">
                        <span class="option_letter_badge">{{ getOptionLetter(index) }}</span>
                        <div class="option_text" v-html="item.answer" />
                      </div>

                      <!-- Tag de retroalimentación según estado -->
                      <span
                        v-if="item.isSelected && isOptionTargetCorrect(item, props.row.successAnswers)"
                        class="option_tag is_success"
                      >
                        <i class="fas fa-check-circle" /> Tu respuesta (Correcta)
                      </span>
                      <span
                        v-else-if="item.isSelected && !isOptionTargetCorrect(item, props.row.successAnswers)"
                        class="option_tag is_danger"
                      >
                        <i class="fas fa-times-circle" /> Tu respuesta (Incorrecta)
                      </span>
                      <span
                        v-else-if="!item.isSelected && isOptionTargetCorrect(item, props.row.successAnswers)"
                        class="option_tag is_target"
                      >
                        <i class="fas fa-check" /> Opción correcta
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <!-- Número de pregunta -->
            <el-table-column
              label="#"
              width="60"
              align="center"
            >
              <template #default="scope">
                <span class="question_index_badge">{{ scope.$index + 1 }}</span>
              </template>
            </el-table-column>

            <!-- Enunciado de la pregunta -->
            <el-table-column
              label="Pregunta"
              min-width="260"
            >
              <template #default="scope">
                <div class="question_table_text" v-html="scope.row.question" />
              </template>
            </el-table-column>

            <!-- Estado de respuesta -->
            <el-table-column
              label="Resultado"
              width="140"
              align="center"
            >
              <template #default="scope">
                <span
                  v-if="isAnswerSuccess(scope.row.selectedAnswers, scope.row.successAnswers)"
                  class="question_status_badge is_success"
                >
                  <i class="fas fa-check-circle" /> Correcta
                </span>
                <span
                  v-else
                  class="question_status_badge is_danger"
                >
                  <i class="fas fa-times-circle" /> Incorrecta
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <template #footer>
        <div class="student_modal_footer">
          <button
            type="button"
            class="student_modal_btn_cancel"
            @click="onCancel"
          >
            <i class="fas fa-times" /> Cerrar
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>
